import '../../../styles/restScreens.css';
import { useCallback, useEffect, useState } from 'react';
import { supabaseClient } from '../../../config/supabase';
import { ACCESS_LABELS, changeShareAccess, fetchColleagues, fetchMyShares, revokeShare, shareWith } from '../../../lib/personalContacts';
import { toneTextStyle } from '../clientsConstants';

import { BTN } from '../../ui/platformStandard';
// BL-177 PR D2: the owner shares ONE contact (contact set) or the WHOLE list (contact null) with named colleagues in the same company,
// View only or Can edit, and can revoke at any time. Only the owner sees this panel; the database refuses anyone else.
const selectCls = 'rounded border border-[var(--surface-border)] bg-[var(--surface-sunken)] px-2 py-1.5 text-sm text-[var(--ink)] outline-none focus:border-brand-500';

export default function PersonalSharePanel({ userId, contact = null, onChanged, client = supabaseClient }) {
    const [colleagues, setColleagues] = useState([]);
    const [shares, setShares] = useState([]);
    const [pick, setPick] = useState('');
    const [access, setAccess] = useState('view');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);

    const scopeId = contact ? contact.id : null;
    const load = useCallback(async () => {
        try {
            const [people, mine] = await Promise.all([fetchColleagues(client), fetchMyShares(client, userId)]);
            setColleagues(people);
            setShares(mine.filter(share => share.contact_id === scopeId));
        } catch (err) { setError(err.message || String(err)); }
    }, [client, userId, scopeId]);
    useEffect(() => { load(); }, [load]);

    const nameOf = (id) => colleagues.find(person => person.id === id)?.name || 'A former colleague';
    const sharedIds = new Set(shares.map(share => share.shared_with));
    const choices = colleagues.filter(person => !sharedIds.has(person.id));

    const run = async (action) => {
        setBusy(true); setError(null);
        try { await action(); await load(); onChanged?.(); } catch (err) { setError(err.message || String(err)); } finally { setBusy(false); }
    };

    return (
        <section data-m6-layout="clients" data-m6-component="PersonalSharePanel" className="mt-3 rounded-lg border border-[var(--surface-border)] bg-[var(--surface-sunken)] p-3" aria-label={contact ? `Share ${contact.name}` : 'Share my whole list'} data-testid="share-panel">
            <div className="text-sm font-semibold text-[var(--ink)]">{contact ? `Share ${contact.name}` : 'Share my whole list'}</div>
            <p className="mt-0.5 text-xs text-[var(--ink-muted)]">
                {contact
                    ? 'Only the people you pick can see this contact. Admins cannot see it unless you share it with them.'
                    : 'Everyone you pick sees every contact in your list, including ones you add later. Revoke any time.'}
            </p>
            <ul className="mt-2 space-y-1.5">
                {shares.length === 0 && <li className="text-sm italic text-[var(--ink-muted)]">Not shared with anyone.</li>}
                {shares.map(share => (
                    <li key={share.id} className="flex flex-wrap items-center gap-2 text-sm text-[var(--ink)]">
                        <span className="min-w-[8rem] font-medium">{nameOf(share.shared_with)}</span>
                        <select className={selectCls} aria-label={`Access for ${nameOf(share.shared_with)}`} value={share.access} disabled={busy}
                            onChange={event => run(() => changeShareAccess(client, share.id, event.target.value))}>
                            <option value="view">{ACCESS_LABELS.view}</option>
                            <option value="edit">{ACCESS_LABELS.edit}</option>
                        </select>
                        <button type="button" disabled={busy} onClick={() => run(() => revokeShare(client, share.id))}
                            className={BTN} aria-label={`Revoke ${nameOf(share.shared_with)}`}>
                            Revoke
                        </button>
                    </li>
                ))}
            </ul>
            <div className="mt-3 flex flex-wrap items-center gap-2">
                <select className={`${selectCls} min-w-[10rem]`} aria-label="Colleague to share with" value={pick} onChange={event => setPick(event.target.value)}>
                    <option value="">Choose a colleague…</option>
                    {choices.map(person => <option key={person.id} value={person.id}>{person.name}</option>)}
                </select>
                <select className={selectCls} aria-label="Access level" value={access} onChange={event => setAccess(event.target.value)}>
                    <option value="view">{ACCESS_LABELS.view}</option>
                    <option value="edit">{ACCESS_LABELS.edit}</option>
                </select>
                <button type="button" disabled={!pick || busy}
                    onClick={() => run(async () => { await shareWith(client, { ownerId: userId, contactId: scopeId, userId: pick, access }); setPick(''); })}
                    className={BTN}>
                    Share
                </button>
            </div>
            {error && <div role="alert" className="mt-2 text-sm" style={toneTextStyle('alert')}>{error}</div>}
        </section>
    );
}
